import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/Group";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import React from "react";

import { CONVERSATION_LENS_PACK } from "../../../../utils/terminology";
import { AnalysisEntity, AnalysisProfile, LensGroup } from "../../types";

interface ProfileTableProps {
  profiles: AnalysisProfile[];
  entities: AnalysisEntity[];
  lensGroups: LensGroup[];
  isLoading?: boolean;
  onEditProfile: (profile: AnalysisProfile) => void;
  onDeleteProfile: (id: string) => void;
  onViewMembers: (profileId: string) => void;
  onViewLensGroup: (lens_group_name: string | null) => void;
}

export const ProfileTable: React.FC<ProfileTableProps> = ({
  profiles,
  entities,
  lensGroups,
  isLoading = false,
  onEditProfile,
  onDeleteProfile,
  onViewMembers,
  onViewLensGroup
}) => {
  const getTotalMembers = (profileId: string) => {
    return entities.filter((e) => e.analysis_profile_id === profileId).length;
  };

  const getLensGroupName = (lens_group_name: string | null) => {
    if (!lens_group_name) {
      return "None";
    }
    const group = lensGroups.find((g) => g.name === lens_group_name);
    return group ? `${group.name} (${group.source})` : lens_group_name;
  };

  if (!isLoading && profiles.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={8}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No analysis groups found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create analysis groups to organize people and assign {CONVERSATION_LENS_PACK}s
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={8}
      >
        <Typography variant="body2" color="text.secondary">
          Loading analysis groups...
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#f5f5f5",
              "& .MuiTableCell-root": {
                paddingTop: "16px",
                paddingBottom: "16px",
                fontWeight: 600
              }
            }}
          >
            <TableCell>Group Name</TableCell>
            <TableCell>Central Inbox Email</TableCell>
            <TableCell>People</TableCell>
            <TableCell>{CONVERSATION_LENS_PACK}</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {profiles.map((profile) => {
            const totalMembers = getTotalMembers(profile.id);

            return (
              <TableRow
                key={profile.id}
                hover
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  height: 52
                }}
              >
                <TableCell>
                  <Box
                    onClick={() => onEditProfile(profile)}
                    sx={{
                      cursor: "pointer",
                      display: "inline-block"
                    }}
                  >
                    {profile.name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box
                    onClick={() => onEditProfile(profile)}
                    sx={{
                      cursor: "pointer",
                      display: "inline-block"
                    }}
                  >
                    {profile.central_inbox_email}
                  </Box>
                </TableCell>
                <TableCell>
                  {totalMembers > 0 ? (
                    <Button
                      size="small"
                      startIcon={<GroupIcon />}
                      onClick={() => onViewMembers(profile.id)}
                      sx={{ textTransform: "none" }}
                    >
                      {totalMembers}{" "}
                      {totalMembers === 1 ? "person" : "people"}
                    </Button>
                  ) : (
                    <Chip
                      label="0 people"
                      size="small"
                      variant="outlined"
                      icon={<GroupIcon />}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {profile.lens_group_name ? (
                    <Chip
                      label={getLensGroupName(profile.lens_group_name)}
                      size="small"
                      color="secondary"
                      onClick={() => onViewLensGroup(profile.lens_group_name!)}
                      icon={<VisibilityIcon />}
                      sx={{ cursor: "pointer" }}
                    />
                  ) : (
                    <Tooltip title="No conversation lens pack is selected and this group will use the industry defaults">
                      <Chip
                        label="None"
                        size="small"
                        onClick={() => onViewLensGroup(null)}
                        icon={<VisibilityIcon />}
                        sx={(theme) => {
                          const bgColor = alpha(theme.palette.info.light, 0.15);
                          const hoverBgColor = alpha(theme.palette.info.light, 0.25);
                          return {
                            cursor: "pointer",
                            backgroundColor: bgColor,
                            border: "none",
                            color: theme.palette.info.dark,
                            "& .MuiChip-icon": {
                              color: theme.palette.info.dark
                            },
                            "&:hover": {
                              backgroundColor: hoverBgColor
                            }
                          };
                        }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={profile.status}
                    size="small"
                    color={profile.status === "active" ? "success" : "default"}
                    onClick={() => onEditProfile(profile)}
                    sx={{ cursor: "pointer" }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => onEditProfile(profile)}
                    color="primary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onDeleteProfile(profile.id)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
